import React, { useState } from 'react';
import { FileIcon, DownloadIcon, ExternalLinkIcon, TrashIcon, FileSymlinkIcon, FolderIcon, CalendarIcon, UserIcon, TagIcon, LinkIcon, FolderTreeIcon, DatabaseIcon, ShieldIcon, ClockIcon, EditIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
export function FileDetails({
  file,
  onClose,
  onDownload,
  onMove,
  formatFileSize,
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'relationships', 'history'
  const [copied, setCopied] = useState(false);
  // Generate a realistic nanoid-like string
  const generateNanoid = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  // Create a deterministic nanoid-like string based on the file ID
  const getNanoid = fileId => {
    // Use the file ID to seed a deterministic but nanoid-like string
    const seed = fileId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      // Use a deterministic approach to generate characters based on the seed
      const charIndex = seed * (i + 1) % chars.length;
      result += chars.charAt(Math.abs(charIndex));
    }
    return result;
  };
  // Generate file URL with the new format and proper nanoid
  const fileUrl = `https://demo.rapidp2p.io/files/${getNanoid(file.id)}`;
  // Handle copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  // Get file type icon
  const getFileTypeIcon = fileType => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileIcon className="h-5 w-5 text-red-500" data-id="element-5645" />;
      case 'docx':
      case 'doc':
        return <FileIcon className="h-5 w-5 text-blue-500" data-id="element-5646" />;
      case 'xlsx':
      case 'xls':
        return <FileIcon className="h-5 w-5 text-green-500" data-id="element-5647" />;
      case 'pptx':
      case 'ppt':
        return <FileIcon className="h-5 w-5 text-orange-500" data-id="element-5648" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileIcon className="h-5 w-5 text-purple-500" data-id="element-5649" />;
      case 'zip':
      case 'rar':
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-5650" />;
      default:
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-5651" />;
    }
  };
  return <div className="h-full flex flex-col" data-id="element-5652">
      {/* File Header */}
      <div className="border-b border-gray-200 pb-4" data-id="element-5653">
        <div className="flex items-center" data-id="element-5654">
          {getFileTypeIcon(file.type)}
          <h3 className="ml-2 text-lg font-medium text-gray-900 truncate" data-id="element-5655">
            {file.name}
          </h3>
        </div>
        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-5656">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2" data-id="element-5657">
            {file.type.toUpperCase()}
          </span>
          <span data-id="element-5658">{formatFileSize(file.size)}</span>
        </div>
        {/* File URL Section */}
        <div className="mt-3 flex items-center" data-id="element-5659">
          <div className="flex-1 relative" data-id="element-5660">
            <div className="flex items-center bg-gray-50 rounded-md p-2 pr-10 overflow-hidden" data-id="element-5661">
              <LinkIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mr-1.5" data-id="element-5662" />
              <span className="text-xs text-gray-600 truncate" data-id="element-5663">{fileUrl}</span>
            </div>
            <button onClick={copyToClipboard} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600" title="Copy URL" data-id="element-5664">
              {copied ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-5665" /> : <CopyIcon className="h-4 w-4" data-id="element-5666" />}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200" data-id="element-5667">
        <nav className="-mb-px flex space-x-8" data-id="element-5668">
          <button onClick={() => setActiveTab('details')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5669">
            Details
          </button>
          <button onClick={() => setActiveTab('relationships')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5670">
            Relationships
          </button>
          <button onClick={() => setActiveTab('history')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5671">
            History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 flex-1 overflow-y-auto" data-id="element-5672">
        {activeTab === 'details' && <div className="space-y-4" data-id="element-5673">
            {/* File Info */}
            <div data-id="element-5674">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5675">
                File Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5676">
                <div className="py-3 flex justify-between text-sm" data-id="element-5677">
                  <dt className="text-gray-500" data-id="element-5678">File Name</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" data-id="element-5679">
                    {file.name}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5680">
                  <dt className="text-gray-500" data-id="element-5681">File Type</dt>
                  <dd className="text-gray-900" data-id="element-5682">{file.type.toUpperCase()}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5683">
                  <dt className="text-gray-500" data-id="element-5684">Size</dt>
                  <dd className="text-gray-900" data-id="element-5685">{formatFileSize(file.size)}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5686">
                  <dt className="text-gray-500" data-id="element-5687">Department</dt>
                  <dd className="text-gray-900" data-id="element-5688">{file.department || 'N/A'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5689">
                  <dt className="text-gray-500" data-id="element-5690">Status</dt>
                  <dd className="text-gray-900" data-id="element-5691">{file.status || 'Active'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5692">
                  <dt className="text-gray-500" data-id="element-5693">File ID</dt>
                  <dd className="text-gray-900 font-mono" data-id="element-5694">{file.id}</dd>
                </div>
              </dl>
            </div>

            {/* Dates */}
            <div data-id="element-5695">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5696">Dates</h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5697">
                <div className="py-3 flex justify-between text-sm" data-id="element-5698">
                  <dt className="text-gray-500" data-id="element-5699">Upload Date</dt>
                  <dd className="text-gray-900" data-id="element-5700">
                    {formatDateTime(file.uploadDate, locale)}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5701">
                  <dt className="text-gray-500" data-id="element-5702">Last Modified</dt>
                  <dd className="text-gray-900" data-id="element-5703">
                    {formatDateTime(file.lastModified, locale)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Tags */}
            {file.tags && file.tags.length > 0 && <div data-id="element-5704">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-5705">Tags</h4>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-5706">
                  {file.tags.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5707">
                      <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5708" />
                      {tag}
                    </span>)}
                </div>
              </div>}

            {/* Storage Information */}
            <div data-id="element-5709">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5710">
                Storage Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5711">
                <div className="py-3 flex justify-between text-sm" data-id="element-5712">
                  <dt className="text-gray-500" data-id="element-5713">Location</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" title={file.storageLocation} data-id="element-5714">
                    {file.storageLocation}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5715">
                  <dt className="text-gray-500" data-id="element-5716">Access Level</dt>
                  <dd className="text-gray-900" data-id="element-5717">{file.accessLevel}</dd>
                </div>
              </dl>
            </div>
          </div>}

        {activeTab === 'relationships' && <div className="space-y-4" data-id="element-5718">
            {/* Parent Files */}
            <div data-id="element-5719">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5720">
                Parent Files
              </h4>
              {file.parentFiles && file.parentFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5721">
                  {file.parentFiles.map(parent => <li key={parent.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5722">
                      <div className="flex items-center" data-id="element-5723">
                        <FolderTreeIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5724" />
                        <span className="text-gray-900" data-id="element-5725">{parent.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5726">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5727" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5728">
                  No parent files
                </p>}
            </div>

            {/* Child Files */}
            <div data-id="element-5729">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5730">Child Files</h4>
              {file.childFiles && file.childFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5731">
                  {file.childFiles.map(child => <li key={child.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5732">
                      <div className="flex items-center" data-id="element-5733">
                        <FileSymlinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5734" />
                        <span className="text-gray-900" data-id="element-5735">{child.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5736">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5737" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5738">
                  No child files
                </p>}
            </div>

            {/* Linked Entities */}
            <div data-id="element-5739">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5740">
                Linked Entities
              </h4>
              {file.linkedEntities && file.linkedEntities.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5741">
                  {file.linkedEntities.map(entity => <li key={entity.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5742">
                      <div className="flex items-center" data-id="element-5743">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5744" />
                        <span className="text-gray-900" data-id="element-5745">
                          {entity.name}{' '}
                          <span className="text-gray-500" data-id="element-5746">({entity.type})</span>
                        </span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5747">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5748" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5749">
                  No linked entities
                </p>}
            </div>
          </div>}

        {activeTab === 'history' && <div data-id="element-5750">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5751">File History</h4>
            <ul className="mt-2 border-t border-gray-200 divide-y divide-gray-200" data-id="element-5752">
              <li className="py-3" data-id="element-5753">
                <div className="flex items-center text-sm" data-id="element-5754">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5755" />
                  <p className="text-gray-900 font-medium" data-id="element-5756">File modified</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5757">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5758" />
                  <span data-id="element-5759">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5760">•</span>
                  <span data-id="element-5761">{formatDateTime(file.lastModified, locale)}</span>
                </div>
              </li>
              <li className="py-3" data-id="element-5762">
                <div className="flex items-center text-sm" data-id="element-5763">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5764" />
                  <p className="text-gray-900 font-medium" data-id="element-5765">File uploaded</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5766">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5767" />
                  <span data-id="element-5768">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5769">•</span>
                  <span data-id="element-5770">{formatDateTime(file.uploadDate, locale)}</span>
                </div>
              </li>
            </ul>
          </div>}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 border-t border-gray-200 pt-4 flex justify-between" data-id="element-5771">
        <div data-id="element-5772">
          <button onClick={() => onDownload(file.id)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5773">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-5774" />
            Download
          </button>
        </div>
        <div className="flex space-x-2" data-id="element-5775">
          <button onClick={() => onMove(file)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5776">
            <FolderIcon className="h-4 w-4 mr-1" data-id="element-5777" />
            Move
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5778">
            <EditIcon className="h-4 w-4 mr-1" data-id="element-5779" />
            Edit
          </button>
        </div>
      </div>
    </div>;
}